// client/src/hooks/useRestaurants.js
import { useState, useEffect } from "react";

const LOCAL_URL = "/api/restaurants";
const PROD_URL = "https://api.mygourmeto.com/api/restaurants";
const RAILWAY_URL =
  "https://restaurant-app-backend-16539hd.up.railway.app/api/restaurants";

const STORAGE_KEY = "gourmeto.restaurants.v1";
// Serve cached places instantly, then refresh in the background once stale.
const FRESH_FOR_MS = 15 * 60 * 1000;
const MAX_PERSIST_BYTES = 3_000_000;

function candidateUrls() {
  if (typeof window === "undefined") return [LOCAL_URL];
  const h = window.location.hostname;
  const isProd =
    h === "mygourmeto.com" ||
    h === "www.mygourmeto.com" ||
    h.endsWith(".vercel.app");
  if (isProd) return [PROD_URL, RAILWAY_URL];
  return [LOCAL_URL, PROD_URL, RAILWAY_URL];
}

// Shared across every useRestaurants() consumer so the list is fetched and
// parsed once per session instead of once per component.
let cache = null; // { data, savedAt }
let inflight = null;
let hydrated = false;

function readStoredCache() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data) || !parsed.data.length) return null;
    return { data: parsed.data, savedAt: Number(parsed.savedAt) || 0 };
  } catch {
    return null;
  }
}

function writeStoredCache(entry) {
  try {
    const raw = JSON.stringify(entry);
    if (raw.length > MAX_PERSIST_BYTES) return;
    window.localStorage.setItem(STORAGE_KEY, raw);
  } catch {
    // Private mode or quota exceeded — the in-memory cache still applies.
  }
}

function getCache() {
  if (!hydrated) {
    hydrated = true;
    if (!cache && typeof window !== "undefined") cache = readStoredCache();
  }
  return cache;
}

function fetchRestaurants() {
  if (inflight) return inflight;

  inflight = (async () => {
    let lastError = null;
    for (const url of candidateUrls()) {
      try {
        const res = await fetch(url, {
          credentials: url.startsWith("/") ? "include" : "omit",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          lastError = new Error(`HTTP ${res.status}`);
          continue;
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        cache = { data: list, savedAt: Date.now() };
        if (list.length) writeStoredCache(cache);
        return list;
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error("Failed to load restaurants");
  })();

  inflight.catch(() => {}).then(() => {
    inflight = null;
  });

  return inflight;
}

const useRestaurants = () => {
  const initial = getCache();
  const [restaurants, setRestaurants] = useState(initial ? initial.data : []);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const current = getCache();

    if (current && Date.now() - current.savedAt < FRESH_FOR_MS) {
      setRestaurants(current.data);
      setLoading(false);
      return undefined;
    }

    fetchRestaurants()
      .then((list) => {
        if (!alive) return;
        setRestaurants(list);
        setError(null);
      })
      .catch((err) => {
        if (alive) setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return { restaurants, loading, error };
};

export default useRestaurants;
